import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, User, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const submitLockRef = useRef(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const cooldownTimerRef = useRef(null);
  const [emailError, setEmailError] = useState('');
  const weakPasswords = new Set([
    'password',
    'password1',
    'password123',
    '12345678',
    'qwerty123',
    'letmein',
    'iloveyou',
    'admin',
    'admin123',
  ]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    termsAccepted: false,
    marketingConsent: false,
  });

  const passwordTrimmed = (formData.password || '').trim();
  const passwordLower = passwordTrimmed.toLowerCase();
  const ruleMinLen = passwordTrimmed.length >= 8;
  const ruleUpper = /[A-Z]/.test(passwordTrimmed);
  const ruleDigit = /[0-9]/.test(passwordTrimmed);
  const ruleNotWeak = passwordTrimmed.length > 0 && !weakPasswords.has(passwordLower);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || submitLockRef.current) return;
    if (cooldownSeconds > 0) {
      toast.error(`Çok fazla deneme. Lütfen ${cooldownSeconds} saniye bekleyin.`);
      return;
    }
    submitLockRef.current = true;
    setEmailError('');

    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Lütfen zorunlu alanları doldurun');
      submitLockRef.current = false;
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      submitLockRef.current = false;
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Şifre en az 8 karakter olmalıdır');
      submitLockRef.current = false;
      return;
    }

    if (!/[A-Z]/.test(formData.password)) {
      toast.error('Şifre en az 1 büyük harf içermelidir');
      submitLockRef.current = false;
      return;
    }

    if (!/[0-9]/.test(formData.password)) {
      toast.error('Şifre en az 1 rakam içermelidir');
      submitLockRef.current = false;
      return;
    }

    if (weakPasswords.has((formData.password || '').trim().toLowerCase())) {
      toast.error('Bu şifre çok zayıf. Lütfen daha güçlü bir şifre seçin.');
      submitLockRef.current = false;
      return;
    }

    if (!formData.termsAccepted) {
      toast.error('Kullanım koşullarını kabul etmelisiniz');
      submitLockRef.current = false;
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await register(formData);
      if (result.success) {
        setEmailError('');
        if (result.pendingEmailConfirmation) {
          toast.success('Üyelik oluşturuldu. Lütfen e-posta adresinizi doğrulayın ve sonra giriş yapın.');
          navigate('/giris');
        } else {
          toast.success('Üyelik başarıyla oluşturuldu!');
          navigate('/');
        }
      } else {
        let msg = result.error;
        if (msg && typeof msg === 'object') {
          msg = msg?.detail || msg?.message || JSON.stringify(msg);
        }
        msg = msg || 'Üyelik oluşturulamadı';

        if (
          typeof msg === 'string' &&
          (msg.toLowerCase().includes('zaten kayıtlı') || msg.toLowerCase().includes('e-posta'))
        ) {
          setEmailError(msg);
        }

        if (msg.toLowerCase().includes('too many requests') || msg.includes('429')) {
          toast.error('Çok fazla deneme yapıldı. Lütfen 1-2 dakika bekleyip tekrar deneyin.');
        } else {
          toast.error(msg);
        }
      }
    } catch (err) {
      let msg = err?.message || err;
      if (msg && typeof msg === 'object') {
        msg = msg?.detail || msg?.message || JSON.stringify(msg);
      }
      msg = msg || 'Üyelik oluşturulamadı';

      if (
        typeof msg === 'string' &&
        (msg.toLowerCase().includes('zaten kayıtlı') || msg.toLowerCase().includes('e-posta'))
      ) {
        setEmailError(msg);
      }
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
      submitLockRef.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4" data-testid="register-page">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <h1 className="font-manrope font-bold text-3xl text-trendyol-orange">Alışveriş Burada</h1>
            </Link>
            <p className="text-gray-600 mt-2">Üyelik Oluşturun</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ad Soyad *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-trendyol-orange focus:outline-none transition-colors pl-11"
                  placeholder="Adınız ve soyadınız"
                  data-testid="register-name-input"
                />
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                E-posta *
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setEmailError('');
                    setFormData({ ...formData, email: e.target.value });
                  }}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-trendyol-orange focus:outline-none transition-colors pl-11"
                  placeholder="ornek@email.com"
                  data-testid="register-email-input"
                />
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
              {emailError ? (
                <div className="mt-2 text-xs text-red-600">
                  {emailError}
                </div>
              ) : null}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Telefon
              </label>
              <div className="relative">
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-trendyol-orange focus:outline-none transition-colors pl-11"
                  placeholder="+90 555 123 45 67"
                  data-testid="register-phone-input"
                />
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Şifre *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-trendyol-orange focus:outline-none transition-colors"
                  placeholder="En az 8 karakter"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="mt-2 text-xs">
                <div className={ruleMinLen ? 'text-green-600' : 'text-gray-600'}>
                  En az 8 karakter
                </div>
                <div className={ruleUpper ? 'text-green-600' : 'text-gray-600'}>
                  En az 1 büyük harf
                </div>
                <div className={ruleDigit ? 'text-green-600' : 'text-gray-600'}>
                  En az 1 rakam
                </div>
                <div className={ruleNotWeak ? 'text-green-600' : 'text-gray-600'}>
                  Zayıf şifre listesinde olmamalı
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Şifre Tekrar *
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-trendyol-orange focus:outline-none transition-colors"
                placeholder="Şifrenizi tekrar girin"
                data-testid="register-confirm-password-input"
              />
            </div>

            <div className="space-y-3">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.termsAccepted}
                  onChange={(e) => setFormData({ ...formData, termsAccepted: e.target.checked })}
                  className="w-4 h-4 text-trendyol-orange border-gray-300 rounded focus:ring-trendyol-orange mt-0.5"
                  data-testid="terms-checkbox"
                />
                <span className="ml-2 text-sm text-gray-600">
                  <Link to="#" className="text-trendyol-orange hover:underline">Kullanım Koşulları</Link> ve{' '}
                  <Link to="#" className="text-trendyol-orange hover:underline">Gizlilik Politikası</Link>'nı okudum, kabul ediyorum. *
                </span>
              </label>

              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.marketingConsent}
                  onChange={(e) => setFormData({ ...formData, marketingConsent: e.target.checked })}
                  className="w-4 h-4 text-trendyol-orange border-gray-300 rounded focus:ring-trendyol-orange mt-0.5"
                  data-testid="marketing-checkbox"
                />
                <span className="ml-2 text-sm text-gray-600">
                  Kampanya ve duyurulardan haberdar olmak istiyorum.
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || cooldownSeconds > 0}
              className="w-full bg-trendyol-orange hover:bg-orange-600 text-white font-semibold py-3 rounded-lg transition-colors"
              data-testid="register-submit-button"
            >
              {cooldownSeconds > 0 ? `Bekleyin (${cooldownSeconds}s)` : 'Üye Ol'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Zaten üye misiniz?{' '}
              <Link to="/giris" className="text-trendyol-orange font-semibold hover:underline" data-testid="login-link">
                Giriş Yap
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-8 bg-trendyol-orange-soft rounded-xl p-6">
          <h3 className="font-semibold text-gray-800 mb-3">Üyelik Avantajları</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-center gap-2">
              <span className="text-trendyol-orange">✓</span>
              Hızlı ve kolay alışveriş
            </li>
            <li className="flex items-center gap-2">
              <span className="text-trendyol-orange">✓</span>
              Sipariş takibi
            </li>
            <li className="flex items-center gap-2">
              <span className="text-trendyol-orange">✓</span>
              Özel indirim ve kampanyalar
            </li>
            <li className="flex items-center gap-2">
              <span className="text-trendyol-orange">✓</span>
              Favorilere ekleme
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}